import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Alert, alpha, Box, Chip, LinearProgress, Skeleton, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';

import { useToast } from '../../../common/hooks/useToast';
import { Item } from '../../../styles/common';
import { CrmPageFrame } from '../CrmPageFrame';
import { ContactPager } from '../contacts/ContactListControls';
import { MatchReviewCard } from './MatchReviewCard';
import { CRM_MATCH_REVIEW } from './matchReview.queries';
import { ReviewDialog } from './ReviewDialog';
import {
  describeQueueError,
  MatchItem,
  MatchReviewState,
  parseReviewState,
  reviewOfTab,
  ReviewState,
  RuleCount,
  RULES,
  ruleLabel,
  signupName,
  TABS,
  toReviewParams,
  withReviewChange,
} from './matchReview';

interface MatchReviewData {
  nbMatchReviewQueue: { total: number; byRule: Array<RuleCount>; items: Array<MatchItem> };
  waiting: { total: number };
  approved: { total: number };
  rejected: { total: number };
}

const n = (value: number) => value.toLocaleString('en-US');

const EMPTY: Record<string, { title: string; body: string }> = {
  waiting: { title: 'Nothing waiting', body: 'Every NationBuilder record the match ladder could not decide alone has been decided.' },
  approved: { title: 'Nothing approved yet', body: 'Records an admin decides are the same person as an app member appear here.' },
  rejected: { title: 'Nothing rejected yet', body: 'Records an admin decides are someone else appear here.' },
};

/**
 * Match review (card 23, on card 07's API): the NationBuilder records the
 * match ladder could not link to an app member alone. Each is shown beside
 * the account, or accounts, it may be; an admin approves ("same person"),
 * rejects, or reopens. A name-and-town match needs a note saying what else
 * agrees. Nothing links until approved, and the contacts are not merged here.
 *
 * Every choice is in the URL. One request per page: the tab's rows and counts
 * per rule, and how many each tab holds. A decision evicts the queue, so the
 * page, its counts and the sidebar's count ask again.
 */
export const CrmMatchReview = () => {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => parseReviewState(params), [params]);
  const toast = useToast();
  const [reviewing, setReviewing] = useState<{ item: MatchItem; review: ReviewState } | null>(null);

  const show = useCallback((next: MatchReviewState) => setParams(toReviewParams(next)), [setParams]);
  const change = useCallback((partial: Partial<MatchReviewState>) => show(withReviewChange(state, partial)), [show, state]);

  const { data, previousData, loading, error } = useQuery<MatchReviewData>(CRM_MATCH_REVIEW, {
    variables: {
      review: reviewOfTab(state.tab),
      ...(state.rule ? { rule: state.rule } : {}),
      ...(state.page ? { skip: state.page * state.pageSize } : {}),
      take: state.pageSize,
    },
    notifyOnNetworkStatusChange: true,
  });
  const shown = data ?? previousData;
  const queue = data?.nbMatchReviewQueue;
  const items = shown?.nbMatchReviewQueue.items ?? [];
  const total = queue?.total;
  const firstLoad = loading && !shown;
  const tabCount = (id: string) => (shown ? (shown as unknown as Record<string, { total: number }>)[id]?.total : undefined);

  const closeReview = (changed: boolean) => {
    const was = reviewing;
    setReviewing(null);
    if (!changed || !was) return;
    const name = signupName(was.item.signup, was.item.nbSignupId);
    toast.success(was.review === 'APPROVED' ? `Approved: ${name}.` : was.review === 'REJECTED' ? `Rejected: ${name}.` : `Reopened: ${name}.`);
  };

  return (
    <CrmPageFrame
      title="Match review"
      description="NationBuilder people who may be the same person as an app member. Nothing links until an admin approves."
    >
      <Item sx={{ p: 0, mb: 2, overflow: 'hidden' }}>
        <Tabs
          value={state.tab}
          onChange={(_, tab) => change({ tab })}
          aria-label="Which records"
          sx={{ px: { xs: 0.5, sm: 1 }, minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600, px: 1.5 } }}
        >
          {TABS.map((tab) => {
            const count = tabCount(tab.id);
            return (
              <Tab
                key={tab.id}
                value={tab.id}
                label={
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    {tab.label}
                    {count !== undefined && (
                      <Box
                        component="span"
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          fontSize: '0.75rem',
                          px: 0.75,
                          borderRadius: 999,
                          bgcolor: tab.id === 'waiting' && count > 0 ? '#fbbf24' : '#eef1f5',
                          color: '#111827',
                        }}
                      >
                        {n(count)}
                      </Box>
                    )}
                  </Box>
                }
              />
            );
          })}
        </Tabs>
        {shown && shown.nbMatchReviewQueue.byRule.length > 0 && (
          <Box
            role="group"
            aria-label="By rule"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: { xs: 1.5, sm: 2 }, py: 1.25, borderTop: '1px solid #eef1f5' }}
          >
            <Chip
              size="small"
              clickable
              aria-pressed={state.rule == null}
              color={state.rule == null ? 'primary' : 'default'}
              variant={state.rule == null ? 'filled' : 'outlined'}
              label={`Every rule ${n(shown.nbMatchReviewQueue.byRule.reduce((sum, r) => sum + r.count, 0))}`}
              onClick={() => change({ rule: null })}
            />
            {shown.nbMatchReviewQueue.byRule.map((r) => (
              <Tooltip key={r.rule} describeChild title={RULES[r.rule]?.hint ?? ''}>
                <Chip
                  size="small"
                  clickable
                  aria-pressed={state.rule === r.rule}
                  color={state.rule === r.rule ? 'primary' : 'default'}
                  variant={state.rule === r.rule ? 'filled' : 'outlined'}
                  label={`${ruleLabel(r.rule)} ${n(r.count)}`}
                  onClick={() => change({ rule: state.rule === r.rule ? null : r.rule })}
                />
              </Tooltip>
            ))}
          </Box>
        )}
        {loading ? <LinearProgress aria-label="Loading the queue" sx={{ height: 2 }} /> : <Box sx={{ height: 2 }} />}
      </Item>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {describeQueueError(error)}
        </Alert>
      )}

      {total !== undefined && items.length > 0 && (
        <Typography variant="body2" color="text.secondary" aria-live="polite" sx={{ mb: 1 }}>
          {n(total)} {total === 1 ? 'record' : 'records'}
          {state.rule ? ` · ${ruleLabel(state.rule)}` : ''}. Records that look like one account are next to each other.
        </Typography>
      )}

      {firstLoad && (
        <Stack spacing={2} role="status" aria-label="Loading the queue…">
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="rounded" height={260} />
          ))}
        </Stack>
      )}

      <Stack spacing={2}>
        {items.map((item) => (
          <MatchReviewCard key={item.nbSignupId} item={item} stale={loading} onReview={(it, review) => setReviewing({ item: it, review })} />
        ))}
      </Stack>

      {!loading && !error && items.length === 0 && (
        <Item sx={{ py: 7, px: 2, display: 'grid', justifyItems: 'center', gap: 1, textAlign: 'center' }}>
          <Box
            sx={(theme) => ({
              width: 56,
              height: 56,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(theme.palette.success.main, 0.1),
              color: 'success.main',
              mb: 1,
            })}
          >
            <HowToRegOutlinedIcon />
          </Box>
          <Typography variant="h6" component="p">
            {EMPTY[state.tab].title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
            {EMPTY[state.tab].body}
          </Typography>
        </Item>
      )}

      {total !== undefined && total > state.pageSize && (
        <Item sx={{ mt: 2, px: { xs: 1.5, sm: 2 }, py: 1 }}>
          <ContactPager
            page={state.page}
            pageSize={state.pageSize}
            rowsOnPage={items.length}
            hasNextPage={(state.page + 1) * state.pageSize < total}
            total={{ count: total, exact: true }}
            disabled={loading}
            onPage={(page) => show({ ...state, page })}
            onPageSize={(pageSize) => change({ pageSize })}
          />
        </Item>
      )}

      {reviewing && <ReviewDialog item={reviewing.item} review={reviewing.review} onClose={closeReview} />}
    </CrmPageFrame>
  );
};

